// queries/getTimeline.ts
import datoCMSClient from './datoCMSClient';
import { TimelineItem } from '../types';

const GET_TIMELINE = `
{
  allTimelines {
    name
    timelineType
    title
    techStack
    summaryPoints
    dateRange
  }
}
`;

// Updated fallback data with new experiences and correct date ranges
const FALLBACK_DATA: TimelineItem[] = [
  {
    timelineType: "work",
    name: "Parafin",
    title: "Software Engineer - Backend (Spends team)",
    techStack: "Python, Scala, Backend Systems, APIs, Data Modeling",
    summaryPoints: [
      "Building and improving backend systems for the Spends team, focused on reliability, correctness, and scalability",
      "Implementing features and services in Python and Scala with an emphasis on clean interfaces and observability",
      "Collaborating cross-functionally to ship impactful improvements while maintaining strong operational standards"
    ],
    dateRange: "February 2026 - Present"
  },
  {
    timelineType: "work",
    name: "Scale AI",
    title: "Gen AI Technical Advisor",
    techStack: "Python, LLMs, Code Generation, Testing Frameworks, FAISS, RAG",
    summaryPoints: [
      "Enhanced large language model code generation accuracy by 12% through comprehensive test expansion and refined evaluation criteria across multiple programming languages",
      "Developed and maintained Python-based evaluation infrastructure with isolated testing environments, processing 500+ automated test cases and reducing manual review overhead by 18%",
      "Engineered retrieval-augmented analysis pipeline using chunked data processing and FAISS indexing to streamline multi-gigabyte log analysis, cutting ticket resolution time by 20%",
      "Collaborated with ML teams to refine model performance through systematic testing and feedback implementation"
    ],
    dateRange: "May 2025 - December 2025"
  },
  {
    timelineType: "work",
    name: "Voodies",
    title: "Full Stack AI Engineer",
    techStack: "React, Node.js, Multi-agent Systems, RAG, AWS, Docker, Jenkins",
    summaryPoints: [
      "Architected multi-agent AI workflow for automated content generation, utilizing RAG prompts and API integrations to reduce product description creation time from 30 to 12 minutes",
      "Developed comprehensive React/Node.js management dashboard featuring role-based permissions, JWT authentication, and RESTful APIs, enabling non-technical team members to publish content in under 3 minutes",
      "Containerized application services and established AWS EC2 deployment pipeline with Docker and Jenkins CI/CD, streamlining deployment process from 45 to 30 minutes",
      "Integrated AI-powered content classification and quality assurance systems to maintain consistent product catalog standards"
    ],
    dateRange: "August 2025 - October 2025"
  },
  {
    timelineType: "work",
    name: "M8 Social Platform",
    title: "Lead Backend Developer",
    techStack: "Node.js, AWS, OAuth, RBAC, Redis, Database Optimization",
    summaryPoints: [
      "Boosted API performance by 15% achieving 1,440 requests/second throughput with sub-180ms p95 latency through strategic routing optimization and connection pool management",
      "Deployed OAuth authentication and role-based access control across AWS infrastructure, decreasing authentication-related support requests by 25% while streamlining security audits",
      "Enhanced database performance through query optimization and Redis caching implementation, achieving 22% faster API response times",
      "Led backend architecture decisions and coordinated with frontend teams to ensure seamless user experience across the platform"
    ],
    dateRange: "August 2024 - May 2025"
  },
  {
    timelineType: "work",
    name: "Alchem International",
    title: "Software Engineering Intern",
    techStack: "Python, Django, PostgreSQL, React, CI/CD, Microservices",
    summaryPoints: [
      "Contributed to monolithic architecture migration by transitioning key modules to Python/Django microservices, helping achieve 15% reduction in deployment-related issues",
      "Optimized database performance through PostgreSQL query restructuring and composite indexing, delivering 18% improvement in query execution speed",
      "Enhanced API responsiveness through multithreading implementation and strategic caching, while improving React frontend efficiency to reduce response times by 20%",
      "Integrated automated testing and deployment workflows using Python scripting for continuous integration processes"
    ],
    dateRange: "May 2024 - August 2024"
  },
  {
    timelineType: "work",
    name: "Penn State AI Innovation Lab",
    title: "Product Recommendation System Researcher",
    techStack: "BERT4Rec, Sentence Transformers, LLMs, Google Search API, Python, PyTorch",
    summaryPoints: [
      "Developed a novel product recommendation system using BERT4Rec model and sentence transformers",
      "Implemented LLM-based product ideation algorithm to generate unique product concepts",
      "Created system to evaluate product uniqueness and feasibility using Google Search API",
      "Achieved 78% accuracy in predicting successful product-market fit for new product concepts",
      "Published findings in university research journal"
    ],
    dateRange: "January 2025 - May 2025"
  },
  {
    timelineType: "work",
    name: "Penn State Data Science Lab",
    title: "Environmental Policy Research Analyst",
    techStack: "Python, Selenium, Pandas, Data Mining, NLP, Statistical Analysis",
    summaryPoints: [
      "Designed and implemented a novel Environmental Policy Uncertainty Index using data mining techniques",
      "Developed automated web scrapers using Python and Selenium to collect policy data from governmental sources",
      "Applied natural language processing to quantify policy uncertainty across different sectors",
      "Created visualization dashboards to track policy changes and their impact on environmental metrics",
      "Collaborated with environmental scientists to validate index accuracy with 92% confidence level"
    ],
    dateRange: "September 2024 - January 2025"
  },
  {
    timelineType: "work",
    name: "Penn State Healthcare Innovation Lab",
    title: "Medical LLM Optimization Researcher",
    techStack: "Python, PyTorch, TensorFlow, NLP, Data Privacy",
    summaryPoints: [
      "Developing compact, privacy-focused language models for medical devices",
      "Achieved 80% size reduction with 95% transcription accuracy",
      "Optimized neural network architectures for medical applications",
      "Implemented efficient data compression techniques"
    ],
    dateRange: "January 2024 - May 2024"
  },
  {
    timelineType: "work",
    name: "Penn State University",
    title: "Learning Assistant Program Lead",
    techStack: "Teaching, Leadership, Curriculum Development, Mentoring",
    summaryPoints: [
      "Spearheaded peer tutoring program supporting 200+ students in STEM courses",
      "Coordinated a team of 15 learning assistants across multiple departments",
      "Developed innovative teaching methodologies for complex technical concepts",
      "Created comprehensive study materials and practice assessments"
    ],
    dateRange: "August 2023 - May 2024"
  },
  {
    timelineType: "education",
    name: "Penn State University",
    title: "Bachelor of Science in Computer Science and Mathematics",
    techStack: "",
    summaryPoints: [
      "Minor in Business Administration",
      "GPA: 3.6",
      "Key Courses: Data Structures & Algorithms, Object-Oriented Programming, Software Design, System Programming, Computer Networks, Database Management, Cybersecurity Fundamentals, Discrete Mathematics, Advanced Algorithms"
    ],
    dateRange: "2022 - December 2025"
  },
  {
    timelineType: "education",
    name: "St. Xaviers School",
    title: "High School Diploma in Science and Mathematics",
    techStack: "",
    summaryPoints: [
      "GPA: 3.7",
      "First place in the National Science Olympiad",
      "Best Student Award for academic excellence",
      "President of the Science Club",
      "Led the school's first Robotics Team"
    ],
    dateRange: "2018 - 2022"
  }
];

export async function getTimeline(): Promise<TimelineItem[]> {
  try {
    const data = await datoCMSClient.request<{ allTimelines: TimelineItem[] }>(GET_TIMELINE);
    return data.allTimelines;
  } catch (error) {
    console.warn("Error fetching timeline data, using fallback data:", error);
    return FALLBACK_DATA;
  }
}